import { clamp, easeInOutSine, easeInQuad, lerp } from '../core/math';
import { FLOW } from '../game/config';
import { HOST_SEAT_ID, type Game } from '../game/Game';
import type { CharacterLook, Customer, GameEvent, SpeakerId } from '../game/types';
import {
  fighterSprite,
  miniSprite,
  seatedSprite,
  walkerSprite,
  type Mood,
  MINI_W,
  SEATED_W,
} from './characters';
import { coffeeIcon } from './icons';
import {
  BOOTH_BACK_Y,
  BOOTH_LANE_Y,
  BOOTH_TABLE_Y,
  BOOTH_X,
  COUNTER_Y,
  DOOR,
  DOOR_X,
  FIGHT_X,
  FIGHT_Y,
  FLOOR_Y,
  JUKEBOX,
  SCENE_H,
  SCENE_W,
  STOOL_COUNT,
  STOOL_LANE_Y,
  STOOL_SPRITE_Y,
  STOOL_X,
  WINDOWS,
  WINDOW_BOTTOM,
  WINDOW_TOP,
  seatPosition,
} from './layout';
import { Particles } from './particles';
import { drawPixelText, glyphRows } from './pixelFont';
import {
  createCanvas,
  ctx2d,
  drawSprite,
  mixColor,
  noise,
  pruneSprites,
  px,
  spriteFromDraw,
  spriteFromGrid,
  type Sprite,
} from './sprite';
import { SIGN_EDGE, SIGN_YELLOW, ticketColor } from './theme';

interface Car {
  x: number;
  speed: number;
  color: string;
}

const CHAIR_GRID = [
  '..kkkkkkkk..',
  '..krrrrrrk..',
  '..krRrrrrk..',
  '..krrrrrrk..',
  '..krrrrrRk..',
  '..kkkkkkkk..',
  '...s....s...',
  '...s....s...',
  'kkkkkkkkkkkk',
  'krrrrrrrrRRk',
  'kkkkkkkkkkkk',
  '.s.s....s.s.',
  '.s..s..s..s.',
  '.s..s..s..s.',
  's...s..s...s',
  's...s..s...s',
];

const LETTERS_TOP = 'WAFFLE';
const LETTERS_BOTTOM = 'HOUSE';

export class SceneRenderer {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly wall: HTMLCanvasElement;
  private readonly tables: HTMLCanvasElement;
  private readonly counter: HTMLCanvasElement;
  private readonly particles = new Particles();
  private readonly talking = new Map<SpeakerId, number>();
  private readonly chair: Sprite;
  private t = 0;
  private shake = 0;
  private flash = 0;
  private flashColor = '#ffffff';
  private lightning = 0;
  private powerFlicker = 0;
  private cars: Car[] = [];
  private nextCar = 3;
  reducedMotion = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    canvas.width = SCENE_W;
    canvas.height = SCENE_H;
    this.ctx = ctx2d(canvas);
    this.wall = createCanvas(SCENE_W, SCENE_H);
    this.tables = createCanvas(SCENE_W, SCENE_H);
    this.counter = createCanvas(SCENE_W, SCENE_H);
    this.paintWall(ctx2d(this.wall));
    this.paintTables(ctx2d(this.tables));
    this.paintCounter(ctx2d(this.counter));
    this.chair = spriteFromGrid('chair', CHAIR_GRID, {
      k: '#2a1a10',
      r: '#c0392b',
      R: '#8e2418',
      s: '#b8c0c8',
    });
  }

  /** Forget transient effects from the previous shift. */
  reset(): void {
    this.particles.clear();
    this.talking.clear();
    this.shake = 0;
    this.flash = 0;
    this.lightning = 0;
    this.powerFlicker = 0;
    this.cars = [];
    // Tonight's customers won't be back (except Hank, who is always back).
    pruneSprites(['seated:', 'mini:', 'walker:', 'fighter:', 'ko:']);
  }

  /* ───────────────────────── events → effects ───────────────────────── */

  handle(event: GameEvent, game: Game): void {
    switch (event.type) {
      case 'speech':
        this.talking.set(event.speaker, this.t + Math.min(event.duration, 2));
        break;
      case 'tip': {
        const p = this.seatPoint(event.seatId);
        this.particles.burst(p.x, p.y - 4, 8, {
          colors: ['#ffd23f', '#fff3a8', '#51cf66'],
          speed: 35,
          life: 0.7,
          gravity: 70,
          up: true,
        });
        break;
      }
      case 'ticketServed':
        if (event.perfect) {
          const p = this.seatPoint(event.ticket.seatId);
          this.particles.burst(p.x, p.y - 8, 10, {
            colors: ['#ffffff', '#ffd23f'],
            speed: 30,
            life: 0.9,
            gravity: 0,
            shape: 'spark',
          });
        }
        break;
      case 'hearts': {
        const p = this.seatPoint(event.seatId);
        const colors = event.yes ? ['#ff5fa2', '#ff8fab', '#ffffff'] : ['#7f8c8d'];
        this.particles.burst(p.x, p.y, event.yes ? 18 : 4, {
          colors,
          speed: 22,
          life: 2,
          gravity: -12,
          shape: 'heart',
          up: true,
        });
        break;
      }
      case 'chairResolved':
        if (event.outcome === 'caught' || event.outcome === 'perfect') {
          this.flash = 0.35;
          this.flashColor = '#ffffff';
          const x = game.chair?.targetX ?? 128;
          this.particles.burst(x, 96, 16, {
            colors: ['#ffffff', '#ffd23f'],
            speed: 70,
            life: 0.6,
            gravity: 0,
            shape: 'spark',
          });
        } else {
          this.flash = 0.45;
          this.flashColor = '#ff6b6b';
          this.shake = 0.6;
          const x = game.chair?.targetX ?? 128;
          this.particles.burst(x, 110, 30, {
            colors: ['#c0392b', '#b8c0c8', '#ffffff', '#2a1a10'],
            speed: 110,
            life: 1,
            gravity: 180,
          });
        }
        break;
      case 'sfx':
        if (event.name === 'thunder') this.lightning = 0.5;
        if (event.name === 'crash') this.shake = Math.max(this.shake, 0.5);
        break;
      case 'power':
        this.powerFlicker = 0.6;
        break;
      case 'coffee': {
        const p = seatPosition(event.seatId);
        this.particles.burst(p.x + 8, COUNTER_Y - 6, 5, {
          colors: ['#f5f5f5'],
          speed: 8,
          life: 1,
          gravity: -20,
          up: true,
        });
        break;
      }
      case 'customerLeft':
        if (event.angry) {
          const p = this.seatPoint(event.customer.seatId);
          this.particles.burst(p.x, p.y - 10, 8, {
            colors: ['#dfe6e9', '#b2bec3'],
            speed: 15,
            life: 1,
            gravity: -25,
            up: true,
          });
        }
        break;
      case 'fightPhase':
        if (event.phase === 'fight') this.shake = 0.25;
        break;
      default:
        break;
    }
  }

  private seatPoint(seatId: number): { x: number; y: number } {
    if (seatId < 0) return { x: 128, y: 80 };
    const p = seatPosition(seatId);
    return p.kind === 'stool' ? { x: p.x, y: p.y + 10 } : { x: p.x, y: p.y + 6 };
  }

  /* ───────────────────────── queries for the DOM layer ───────────────────────── */

  /** Customer under a logical scene point (for coffee refills / ticket selection). */
  customerAt(game: Game, lx: number, ly: number): Customer | undefined {
    let best: Customer | undefined;
    let bestD = Infinity;
    for (const c of game.customers.values()) {
      if (c.state !== 'waiting' && c.state !== 'eating') continue;
      if (c.seatId < 0) continue;
      const p = seatPosition(c.seatId);
      const cx = p.x;
      const cy = p.kind === 'stool' ? p.y + 14 : p.y + 8;
      const halfW = p.kind === 'stool' ? 16 : 18;
      const halfH = p.kind === 'stool' ? 20 : 14;
      if (Math.abs(lx - cx) > halfW || Math.abs(ly - cy) > halfH) continue;
      const d = Math.abs(lx - cx) + Math.abs(ly - cy);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    return best;
  }

  /** Head position of whoever is talking (logical px), or null if unknown. */
  speakerAnchor(game: Game, speaker: SpeakerId): { x: number; y: number } | null {
    if (speaker === 'fighterA') return { x: FIGHT_X - 16, y: FIGHT_Y - 16 };
    if (speaker === 'fighterB') return { x: FIGHT_X + 16, y: FIGHT_Y - 16 };
    if (speaker === 'dottie' || speaker === 'crowd') return null;
    const c = game.customers.get(speaker);
    if (!c) return null;
    if (c.seatId === HOST_SEAT_ID) return { x: 128, y: 56 };
    const p = seatPosition(c.seatId);
    return p.kind === 'stool' ? { x: p.x, y: p.y - 1 } : { x: p.x, y: p.y - 3 };
  }

  /* ───────────────────────── frame ───────────────────────── */

  render(game: Game, dt: number, activeTicketId: number | null): void {
    this.t += dt;
    const ctx = this.ctx;
    this.particles.update(dt);
    this.shake = Math.max(0, this.shake - dt);
    this.flash = Math.max(0, this.flash - dt);
    this.lightning = Math.max(0, this.lightning - dt);
    this.powerFlicker = Math.max(0, this.powerFlicker - dt);

    ctx.save();
    if (this.shake > 0 && !this.reducedMotion) {
      const s = Math.ceil(this.shake * 4);
      ctx.translate(Math.round((Math.random() - 0.5) * s), Math.round((Math.random() - 0.5) * s));
    }
    ctx.clearRect(-8, -8, SCENE_W + 16, SCENE_H + 16);

    this.drawOutside(ctx, game, dt);
    ctx.drawImage(this.wall, 0, 0);
    this.drawJukeboxLights(ctx, game);
    this.drawBooths(ctx, game);
    ctx.drawImage(this.tables, 0, 0);
    this.drawBoothFlags(ctx, game, activeTicketId);
    this.drawWalkers(ctx, game, 'booth');
    this.drawFight(ctx, game);
    this.drawWalkers(ctx, game, 'stool');
    this.drawCounterCustomers(ctx, game, activeTicketId);
    ctx.drawImage(this.counter, 0, 0);
    this.drawCounterItems(ctx, game, activeTicketId);
    this.drawHost(ctx, game);
    this.particles.draw(ctx);
    this.drawLighting(ctx, game);
    this.drawChair(ctx, game);

    if (this.flash > 0 && !this.reducedMotion) {
      ctx.globalAlpha = Math.min(0.7, this.flash * 1.6);
      ctx.fillStyle = this.flashColor;
      ctx.fillRect(0, 0, SCENE_W, SCENE_H);
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  }

  /* ───────────────────────── outside the windows ───────────────────────── */

  private drawOutside(ctx: CanvasRenderingContext2D, game: Game, dt: number): void {
    const dawn = clamp((game.minutes - 400) / 80, 0, 1);
    const storm = game.storm !== null;
    const top = mixColor(storm ? '#05070d' : '#070b1f', '#6f9bd6', dawn);
    const bottom = mixColor(storm ? '#141820' : '#231642', '#ffb26b', dawn);

    ctx.save();
    ctx.beginPath();
    for (const w of WINDOWS) ctx.rect(w.x, WINDOW_TOP, w.w, WINDOW_BOTTOM - WINDOW_TOP);
    ctx.rect(DOOR.x + 3, DOOR.y + 3, DOOR.w - 6, 28);
    ctx.clip();

    for (let y = 0; y < WINDOW_BOTTOM; y++) {
      px(ctx, 0, y, SCENE_W, 1, mixColor(top, bottom, y / WINDOW_BOTTOM));
    }
    // Stars
    if (!storm) {
      for (let i = 0; i < 70; i++) {
        const sx = Math.floor(noise(i, 1, 42) * SCENE_W);
        const sy = Math.floor(noise(i, 2, 42) * 26) + WINDOW_TOP;
        const twinkle = noise(i, Math.floor(this.t * 2), 5) > 0.15;
        if (twinkle) {
          ctx.globalAlpha = (1 - dawn) * (0.5 + noise(i, 3, 42) * 0.5);
          px(ctx, sx, sy, 1, 1, '#ffffff');
        }
      }
      ctx.globalAlpha = 1;
      // Moon
      if (dawn < 0.8) {
        ctx.globalAlpha = 1 - dawn;
        px(ctx, 44, 12, 6, 6, '#f8f1d0');
        px(ctx, 43, 13, 8, 4, '#f8f1d0');
        px(ctx, 47, 12, 4, 5, top);
        ctx.globalAlpha = 1;
      }
    } else {
      // Storm clouds
      for (let i = 0; i < 40; i++) {
        const cx = ((Math.floor(noise(i, 9, 7) * 300) + Math.floor(this.t * 6)) % 300) - 20;
        const cy = WINDOW_TOP + Math.floor(noise(i, 8, 7) * 14);
        px(ctx, cx, cy, 14, 4, 'rgba(40,46,60,0.8)');
      }
    }
    // Treeline
    for (let x = 0; x < SCENE_W; x++) {
      const h = 3 + Math.floor(noise(Math.floor(x / 3), 1, 11) * 4);
      px(ctx, x, 35 - h, 1, h, mixColor('#0b1510', '#2d3b2a', dawn));
    }
    // Parking lot & road
    px(ctx, 0, 35, SCENE_W, 11, mixColor('#1b1d24', '#4a4c55', dawn * 0.8));
    for (let x = 6; x < SCENE_W; x += 18) px(ctx, x, 40, 1, 5, '#c9a93a');
    px(ctx, 0, 35, SCENE_W, 1, mixColor('#2a2d36', '#6c6f7a', dawn));
    // Parked cars
    this.drawParkedCar(ctx, 40, 38, '#7f1d1d');
    this.drawParkedCar(ctx, 76, 38, '#1f3a6b');
    this.drawParkedCar(ctx, 182, 38, '#e5e5e5');
    this.drawParkedCar(ctx, 206, 38, '#2d6a4f');
    // Lamp posts
    for (const lx of [66, 196]) {
      px(ctx, lx, 16, 1, 20, '#3b3f47');
      px(ctx, lx - 2, 15, 5, 1, '#3b3f47');
      ctx.globalAlpha = 0.18 * (1 - dawn);
      ctx.fillStyle = '#ffe9a8';
      ctx.beginPath();
      ctx.moveTo(lx - 1, 16);
      ctx.lineTo(lx - 8, 40);
      ctx.lineTo(lx + 8, 40);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
      px(ctx, lx - 1, 16, 3, 1, '#fff3c4');
    }
    // Passing cars
    this.nextCar -= dt;
    if (this.nextCar <= 0) {
      this.nextCar = 6 + Math.random() * 10;
      const dir = Math.random() < 0.5 ? 1 : -1;
      this.cars.push({
        x: dir > 0 ? -30 : SCENE_W + 30,
        speed: dir * (60 + Math.random() * 40),
        color:
          ['#c0392b', '#2980b9', '#95a5a6', '#f1c40f'][Math.floor(Math.random() * 4)] ?? '#999',
      });
    }
    this.cars = this.cars.filter((c) => c.x > -40 && c.x < SCENE_W + 40);
    for (const car of this.cars) {
      car.x += car.speed * dt;
      const y = 42;
      px(ctx, car.x, y, 16, 3, car.color);
      px(ctx, car.x + 3, y - 2, 9, 2, mixColor(car.color, '#000000', 0.3));
      const front = car.speed > 0 ? car.x + 15 : car.x;
      px(ctx, front, y, 1, 1, '#fff7c2');
      ctx.globalAlpha = 0.25;
      px(ctx, car.speed > 0 ? front + 1 : front - 14, y - 1, 14, 3, '#fff3b0');
      ctx.globalAlpha = 1;
    }

    this.drawSign(ctx, game);

    // Rain
    if (storm) {
      ctx.fillStyle = 'rgba(170,190,220,0.55)';
      for (let i = 0; i < 90; i++) {
        const rx = (Math.floor(noise(i, 4, 3) * SCENE_W) + Math.floor(this.t * 40)) % SCENE_W;
        const ry =
          ((Math.floor(noise(i, 5, 3) * 40) + Math.floor(this.t * 120 + i * 3)) % 40) + WINDOW_TOP;
        ctx.fillRect(rx, ry, 1, 3);
      }
    }
    if (this.lightning > 0 && !this.reducedMotion) {
      ctx.globalAlpha = this.lightning > 0.35 ? 0.9 : this.lightning;
      px(ctx, 0, 0, SCENE_W, WINDOW_BOTTOM, '#e8f0ff');
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  }

  private drawParkedCar(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
    px(ctx, x, y + 1, 18, 4, color);
    px(ctx, x + 3, y - 1, 11, 2, mixColor(color, '#000000', 0.25));
    px(ctx, x + 4, y - 1, 4, 1, '#9fb6cc');
    px(ctx, x + 9, y - 1, 4, 1, '#9fb6cc');
    px(ctx, x + 2, y + 5, 3, 1, '#0b0b0b');
    px(ctx, x + 13, y + 5, 3, 1, '#0b0b0b');
  }

  private drawSign(ctx: CanvasRenderingContext2D, game: Game): void {
    const hostOn = game.host !== null && game.host.resolved === null;
    const sway = game.storm && !this.reducedMotion ? Math.round(Math.sin(this.t * 3) * 1) : 0;
    const tile = 7;
    const gap = 1;
    const y0 = 11;
    // Glow
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const glow = ctx.createRadialGradient(128, y0 + 9, 2, 128, y0 + 9, 40);
    glow.addColorStop(0, hostOn ? 'rgba(255,60,40,0.45)' : 'rgba(255,210,63,0.38)');
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(80, 0, 96, 45);
    ctx.restore();
    // Pole
    px(ctx, 127 + sway, y0 + 18, 3, 20, '#2b2e35');
    const powerOut = !game.powerOn;
    const flicker = powerOut && Math.floor(this.t * 12) % 7 === 0;
    const drawRow = (letters: string, rowY: number): void => {
      const rowW = letters.length * (tile + gap) - gap;
      const rx = 128 - Math.floor(rowW / 2) + sway;
      for (let i = 0; i < letters.length; i++) {
        let ch = letters[i] ?? ' ';
        if (hostOn && noise(i, Math.floor(this.t * 10), rowY) > 0.7)
          ch = 'HOST'[Math.floor(noise(i, rowY, Math.floor(this.t * 5)) * 4)] ?? ch;
        const tx = rx + i * (tile + gap);
        const lit = !flicker;
        const face = hostOn
          ? Math.floor(this.t * 8 + i) % 5 === 0
            ? '#ff5a3c'
            : SIGN_YELLOW
          : lit
            ? SIGN_YELLOW
            : '#8a7424';
        px(ctx, tx, rowY, tile, tile, face);
        px(ctx, tx, rowY + tile - 1, tile, 1, SIGN_EDGE);
        const rows = glyphRows(ch);
        rows.forEach((row, gy) => {
          for (let gx = 0; gx < row.length; gx++)
            if (row[gx] === '#') px(ctx, tx + 2 + gx, rowY + 1 + gy, 1, 1, '#141414');
        });
      }
    };
    drawRow(LETTERS_TOP, y0);
    drawRow(LETTERS_BOTTOM, y0 + tile + gap);
  }

  /* ───────────────────────── static interior ───────────────────────── */

  private paintWall(ctx: CanvasRenderingContext2D): void {
    // Upper wall
    px(ctx, 0, 0, SCENE_W, FLOOR_Y, '#e2bd6b');
    for (let y = 0; y < FLOOR_Y; y += 4) px(ctx, 0, y, SCENE_W, 1, '#d8b160');
    // Ceiling trim & lights
    px(ctx, 0, 0, SCENE_W, 4, '#6b4a2b');
    px(ctx, 0, 4, SCENE_W, 1, '#4a3220');
    // Wainscot
    px(ctx, 0, 46, SCENE_W, FLOOR_Y - 46, '#7a4b2a');
    for (let x = 0; x < SCENE_W; x += 6) px(ctx, x, 47, 1, FLOOR_Y - 47, '#6a3f22');
    px(ctx, 0, 46, SCENE_W, 1, '#9a6a3f');
    // Windows (clear the glass so the outside layer shows through)
    for (const w of WINDOWS) {
      px(ctx, w.x - 2, WINDOW_TOP - 2, w.w + 4, WINDOW_BOTTOM - WINDOW_TOP + 4, '#2b1d14');
      ctx.clearRect(w.x, WINDOW_TOP, w.w, WINDOW_BOTTOM - WINDOW_TOP);
      px(ctx, w.x + Math.floor(w.w / 2), WINDOW_TOP, 1, WINDOW_BOTTOM - WINDOW_TOP, '#2b1d14');
      px(ctx, w.x - 2, WINDOW_BOTTOM + 1, w.w + 4, 1, '#c79a5c');
    }
    // Door
    px(ctx, DOOR.x, DOOR.y, DOOR.w, DOOR.h, '#3a2718');
    px(ctx, DOOR.x + 2, DOOR.y + 2, DOOR.w - 4, DOOR.h - 2, '#5b3a22');
    ctx.clearRect(DOOR.x + 3, DOOR.y + 3, DOOR.w - 6, 28);
    px(ctx, DOOR.x + 4, DOOR.y + 34, DOOR.w - 8, 3, '#b8c0c8');
    drawPixelText(ctx, 'PUSH', DOOR.x + 3, DOOR.y + 38, '#e8e0cc');
    px(ctx, DOOR.x + DOOR.w - 6, DOOR.y + 1, 3, 2, '#e0b64a');
    // Jukebox body
    const j = JUKEBOX;
    px(ctx, j.x + 2, j.y, j.w - 4, 2, '#4a2a18');
    px(ctx, j.x, j.y + 2, j.w, j.h - 2, '#5a3320');
    px(ctx, j.x + 2, j.y + 4, j.w - 4, 10, '#1a1320');
    px(ctx, j.x + 3, j.y + 17, j.w - 6, 8, '#2b2320');
    for (let y = j.y + 18; y < j.y + 25; y += 2) px(ctx, j.x + 4, y, j.w - 8, 1, '#4d4038');
    px(ctx, j.x + 2, j.y + j.h - 4, j.w - 4, 3, '#3a2014');
    // Floor
    for (let y = FLOOR_Y; y < SCENE_H; y += 4) {
      for (let x = 0; x < SCENE_W; x += 8) {
        const odd = (x / 8 + (y - FLOOR_Y) / 4) % 2 === 0;
        px(ctx, x, y, 8, 4, odd ? '#9c5a33' : '#86492a');
      }
    }
    px(ctx, 0, FLOOR_Y, SCENE_W, 1, '#3a2418');
    // Booth backs
    for (const bx of BOOTH_X) {
      px(ctx, bx - 22, BOOTH_BACK_Y, 44, 3, '#b83a3a');
      px(ctx, bx - 22, BOOTH_BACK_Y + 3, 44, FLOOR_Y - BOOTH_BACK_Y - 3, '#8e2424');
      for (let x = bx - 20; x < bx + 22; x += 7) px(ctx, x, BOOTH_BACK_Y + 4, 1, 10, '#7a1d1d');
      px(ctx, bx - 22, FLOOR_Y - 2, 44, 2, '#5a1616');
    }
  }

  private paintTables(ctx: CanvasRenderingContext2D): void {
    for (const bx of BOOTH_X) {
      px(ctx, bx - 20, BOOTH_TABLE_Y, 40, 3, '#d9b07a');
      px(ctx, bx - 20, BOOTH_TABLE_Y + 3, 40, 1, '#8a6238');
      px(ctx, bx - 2, BOOTH_TABLE_Y + 4, 4, FLOOR_Y - BOOTH_TABLE_Y - 2, '#3a3a3a');
      // napkin dispenser & syrup
      px(ctx, bx + 12, BOOTH_TABLE_Y - 3, 4, 3, '#b8c0c8');
      px(ctx, bx - 15, BOOTH_TABLE_Y - 4, 2, 4, '#7a3b12');
    }
  }

  private paintCounter(ctx: CanvasRenderingContext2D): void {
    px(ctx, 0, COUNTER_Y, SCENE_W, 5, '#efe2c4');
    px(ctx, 0, COUNTER_Y, SCENE_W, 1, '#fff6e0');
    px(ctx, 0, COUNTER_Y + 5, SCENE_W, 2, '#c9b58f');
    px(ctx, 0, COUNTER_Y + 7, SCENE_W, 2, '#b8c0c8');
    px(ctx, 0, COUNTER_Y + 9, SCENE_W, SCENE_H - COUNTER_Y - 9, '#2d2622');
    for (let x = 0; x < SCENE_W; x += 32)
      px(ctx, x, COUNTER_Y + 9, 1, SCENE_H - COUNTER_Y - 9, '#241e1a');
    px(ctx, 0, COUNTER_Y + 13, SCENE_W, 1, '#3a322d');
    // Napkin dispensers and condiment caddies between stools
    for (let i = 0; i < STOOL_COUNT - 1; i++) {
      const x = ((STOOL_X[i] ?? 0) + (STOOL_X[i + 1] ?? 0)) / 2;
      if (i % 2 === 0) {
        px(ctx, x - 3, COUNTER_Y - 5, 6, 5, '#b8c0c8');
        px(ctx, x - 2, COUNTER_Y - 6, 4, 1, '#ffffff');
        px(ctx, x - 3, COUNTER_Y - 5, 1, 5, '#8a929a');
      } else {
        px(ctx, x - 4, COUNTER_Y - 6, 2, 6, '#c0392b');
        px(ctx, x - 1, COUNTER_Y - 7, 2, 7, '#7a3b12');
        px(ctx, x - 1, COUNTER_Y - 8, 2, 1, '#b8c0c8');
        px(ctx, x + 2, COUNTER_Y - 4, 2, 4, '#f5f5f5');
        px(ctx, x + 2, COUNTER_Y - 5, 2, 1, '#95a5a6');
      }
    }
  }

  private drawJukeboxLights(ctx: CanvasRenderingContext2D, game: Game): void {
    const j = JUKEBOX;
    const off = !game.powerOn;
    const hues = ['#ff5f5f', '#ffd23f', '#5fd3ff', '#b56bff', '#5fff9f'];
    for (let i = 0; i < 5; i++) {
      const c = off ? '#2a1f1a' : (hues[(i + Math.floor(this.t * 3)) % hues.length] ?? '#fff');
      px(ctx, j.x + 1, j.y + 3 + i * 6, 1, 5, c);
      px(ctx, j.x + j.w - 2, j.y + 3 + i * 6, 1, 5, c);
    }
    if (!off) {
      const spin = Math.floor(this.t * 6) % 4;
      px(ctx, j.x + 6, j.y + 6, 8, 6, '#0d0d0d');
      px(ctx, j.x + 9 + (spin % 2), j.y + 8 + (spin > 1 ? 1 : 0), 2, 2, '#ffd23f');
    }
  }

  /* ───────────────────────── people ───────────────────────── */

  private mood(game: Game, c: Customer): Mood {
    if (c.angry) return 'angry';
    if (c.state === 'eating') return Math.floor(this.t * 4 + c.id) % 3 === 0 ? 'eat' : 'happy';
    const talkUntil = this.talking.get(c.id) ?? 0;
    if (talkUntil > this.t) return Math.floor(this.t * 7) % 2 === 0 ? 'talk' : 'normal';
    if (c.ticketId !== null && c.patience < 0.25 && !game.patienceFrozen) return 'angry';
    const blinkPhase = (this.t + c.id * 0.73) % 3.6;
    if (blinkPhase < 0.12) return 'blink';
    return 'normal';
  }

  private companionLook(c: Customer): CharacterLook {
    const base = c.look;
    const hairs = ['#24170f', '#6b4125', '#e2c275', '#a8412b', '#8f8f8f'];
    const shirts = ['#2e86de', '#e84393', '#27ae60', '#f39c12', '#8e44ad'];
    if (c.archetype === 'couple') {
      return {
        ...base,
        hairStyle: 'long',
        hair: hairs[c.id % hairs.length] ?? base.hair,
        shirt: '#e84393',
        shirtStyle: 'plain',
        accessory: 'none',
        facialHair: 'none',
      };
    }
    return {
      ...base,
      hair: hairs[(c.id * 3) % hairs.length] ?? base.hair,
      shirt: shirts[(c.id * 7) % shirts.length] ?? base.shirt,
      hairStyle: c.id % 2 ? 'long' : 'short',
      facialHair: 'none',
    };
  }

  private seatedVisible(c: Customer): boolean {
    if (c.state === 'waiting' || c.state === 'eating') return true;
    return c.state === 'entering' && c.stateTime >= FLOW.walkIn;
  }

  private drawBooths(ctx: CanvasRenderingContext2D, game: Game): void {
    for (const c of game.customers.values()) {
      if (c.seatId < STOOL_COUNT || !this.seatedVisible(c)) continue;
      const p = seatPosition(c.seatId);
      const bob = Math.floor((this.t * 1.5 + c.id) % 2) === 0 ? 0 : 1;
      const mood = this.mood(game, c);
      drawSprite(ctx, miniSprite(c.look, mood), p.x - MINI_W - 1, p.y + bob);
      drawSprite(
        ctx,
        miniSprite(this.companionLook(c), mood === 'talk' ? 'normal' : mood),
        p.x + 1,
        p.y + (1 - bob),
        { flip: true },
      );
      if (c.state === 'eating') {
        px(ctx, p.x - 8, BOOTH_TABLE_Y - 1, 7, 1, '#ffffff');
        px(ctx, p.x - 7, BOOTH_TABLE_Y - 2, 4, 1, '#e7b75a');
        px(ctx, p.x + 2, BOOTH_TABLE_Y - 1, 7, 1, '#ffffff');
        px(ctx, p.x + 3, BOOTH_TABLE_Y - 2, 5, 1, '#f2b64c');
      }
    }
  }

  private drawBoothFlags(
    ctx: CanvasRenderingContext2D,
    game: Game,
    activeTicketId: number | null,
  ): void {
    for (const t of game.openTickets()) {
      if (t.seatId < STOOL_COUNT) continue;
      const p = seatPosition(t.seatId);
      this.drawFlag(ctx, p.x - 5, BOOTH_TABLE_Y - 8, t.no, t.color, t.id === activeTicketId);
    }
  }

  private drawFlag(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    no: number,
    color: number,
    active: boolean,
  ): void {
    const label = String(no % 100);
    const w = label.length * 4 + 3;
    px(ctx, x, y + 7, 1, 2, '#b8c0c8');
    px(
      ctx,
      x,
      y,
      w,
      7,
      active && Math.floor(this.t * 4) % 2 === 0 ? '#ffffff' : ticketColor(color),
    );
    px(ctx, x, y + 6, w, 1, mixColor(ticketColor(color), '#000000', 0.35));
    drawPixelText(ctx, label, x + 2, y + 1, '#111111');
  }

  private walkerPoint(c: Customer): { x: number; y: number; frame: 0 | 1 } | null {
    if (c.seatId < 0) return null;
    const seat = seatPosition(c.seatId);
    const lane = seat.kind === 'stool' ? STOOL_LANE_Y : BOOTH_LANE_Y;
    let p: number;
    if (c.state === 'entering') {
      if (c.stateTime >= FLOW.walkIn) return null;
      p = c.stateTime / FLOW.walkIn;
    } else if (c.state === 'leaving') {
      p = 1 - c.stateTime / FLOW.leave;
    } else {
      return null;
    }
    const x = lerp(DOOR_X, seat.x, easeInOutSine(clamp(p, 0, 1)));
    const frame = (Math.floor(c.stateTime * 8) % 2) as 0 | 1;
    return { x, y: lane, frame };
  }

  private drawWalkers(ctx: CanvasRenderingContext2D, game: Game, lane: 'stool' | 'booth'): void {
    for (const c of game.customers.values()) {
      if (c.seatId < 0) continue;
      const isStool = c.seatId < STOOL_COUNT;
      if ((lane === 'stool') !== isStool) continue;
      const w = this.walkerPoint(c);
      if (!w) continue;
      const sprite = walkerSprite(c.look, w.frame, c.angry);
      const flip = c.state === 'leaving';
      drawSprite(ctx, sprite, w.x - 6, w.y - 24, { flip });
      if (!isStool) {
        drawSprite(
          ctx,
          walkerSprite(this.companionLook(c), w.frame === 0 ? 1 : 0),
          w.x - 16,
          w.y - 24,
          { flip },
        );
      }
    }
  }

  private drawCounterCustomers(
    ctx: CanvasRenderingContext2D,
    game: Game,
    activeTicketId: number | null,
  ): void {
    const hostOn = game.host !== null && game.host.resolved === null;
    for (const c of game.customers.values()) {
      if (c.seatId < 0 || c.seatId >= STOOL_COUNT || !this.seatedVisible(c)) continue;
      const p = seatPosition(c.seatId);
      const angryShake =
        c.ticketId !== null && c.patience < 0.15 && !game.patienceFrozen && !this.reducedMotion
          ? Math.floor(this.t * 20) % 2
          : 0;
      const bob = Math.floor((this.t * 1.2 + c.id * 0.37) % 2) === 0 ? 0 : 1;
      const x = p.x - SEATED_W / 2 + angryShake;
      const y = STOOL_SPRITE_Y + bob + (c.look.species === 'raccoon' ? 2 : 0);
      drawSprite(ctx, seatedSprite(c.look, this.mood(game, c)), x, y);
      if (hostOn) {
        // Everyone stares, eyes glowing.
        px(ctx, x + 7, y + 8, 1, 1, '#ffe14d');
        px(ctx, x + 12, y + 8, 1, 1, '#ffe14d');
      }
      const selected = c.ticketId !== null && c.ticketId === activeTicketId;
      if (selected) {
        const ay = STOOL_SPRITE_Y - 9 + (Math.floor(this.t * 3) % 2);
        const t = game.ticket(c.ticketId);
        const color = t ? ticketColor(t.color) : '#ffffff';
        px(ctx, p.x - 2, ay, 5, 1, color);
        px(ctx, p.x - 1, ay + 1, 3, 1, color);
        px(ctx, p.x, ay + 2, 1, 1, color);
      }
      // Coffee request bubble
      if (c.state === 'waiting' && c.drinksCoffee && c.mug < 0.3) {
        const by = STOOL_SPRITE_Y - 14 + (Math.floor(this.t * 2.5) % 2);
        const bx = p.x + 3;
        px(ctx, bx, by, 14, 12, '#ffffff');
        px(ctx, bx + 1, by - 1, 12, 1, '#ffffff');
        px(ctx, bx + 1, by + 12, 12, 1, '#ffffff');
        px(ctx, bx + 2, by + 13, 2, 2, '#ffffff');
        drawSprite(ctx, coffeeIcon(), bx - 1, by - 2, {
          alpha: c.mug <= 0 ? (Math.floor(this.t * 4) % 2 ? 1 : 0.5) : 1,
        });
      }
    }
  }

  private drawCounterItems(
    ctx: CanvasRenderingContext2D,
    game: Game,
    activeTicketId: number | null,
  ): void {
    for (const c of game.customers.values()) {
      if (c.seatId < 0 || c.seatId >= STOOL_COUNT || !this.seatedVisible(c)) continue;
      const p = seatPosition(c.seatId);
      // Mug
      if (c.drinksCoffee) {
        const mx = p.x + 6;
        const my = COUNTER_Y - 5;
        px(ctx, mx, my, 5, 5, '#fffaf0');
        px(ctx, mx + 5, my + 1, 2, 3, '#fffaf0');
        px(ctx, mx + 5, my + 2, 1, 1, '#2d2622');
        px(ctx, mx, my, 5, 1, c.mug > 0.1 ? '#6b3a1f' : '#d9d3c7');
        if (c.mug > 0.6 && Math.floor(this.t * 2 + c.id) % 3 !== 0) {
          px(ctx, mx + 1 + (Math.floor(this.t * 3) % 2), my - 3, 1, 2, 'rgba(255,255,255,0.5)');
        }
      }
      // Plate while eating
      if (c.state === 'eating') {
        px(ctx, p.x - 8, COUNTER_Y - 1, 13, 1, '#ffffff');
        px(ctx, p.x - 7, COUNTER_Y - 2, 11, 1, '#f3efe6');
        px(ctx, p.x - 6, COUNTER_Y - 3, 5, 1, '#e7b75a');
        px(ctx, p.x - 1, COUNTER_Y - 3, 4, 1, '#f2b64c');
      }
      // Ticket flag
      if (c.ticketId !== null && c.state === 'waiting') {
        const t = game.ticket(c.ticketId);
        if (t) this.drawFlag(ctx, p.x - 12, COUNTER_Y - 9, t.no, t.color, t.id === activeTicketId);
      }
    }
  }

  private drawFight(ctx: CanvasRenderingContext2D, game: Game): void {
    const f = game.fight;
    if (!f) return;
    const [la, lb] = f.looks;
    const cx = FIGHT_X;
    const baseY = FIGHT_Y - 12;
    if (f.phase === 'argue' || f.phase === 'round') {
      const jitter = Math.floor(this.t * 6) % 2;
      drawSprite(ctx, fighterSprite(la, jitter ? 1 : 0), cx - 24, baseY);
      drawSprite(ctx, fighterSprite(lb, jitter ? 0 : 1), cx + 8, baseY, { flip: true });
      return;
    }
    if (f.phase === 'fight') {
      // The classic cartoon dust-cloud brawl.
      const frame = Math.floor(this.t * 12);
      for (let i = 0; i < 9; i++) {
        const a = noise(i, frame, 2) * Math.PI * 2;
        const r = 4 + noise(frame, i, 3) * 12;
        const x = cx + Math.cos(a) * r;
        const y = FIGHT_Y + Math.sin(a) * r * 0.6;
        const size = 8 + Math.floor(noise(i, frame, 9) * 7);
        px(ctx, x - size / 2, y - size / 2, size, size, i % 3 === 0 ? '#d9d9d9' : '#f2f2f2');
      }
      for (let i = 0; i < 4; i++) {
        const a = noise(i, frame, 21) * Math.PI * 2;
        const x = cx + Math.cos(a) * 16;
        const y = FIGHT_Y + Math.sin(a) * 9;
        const look = i % 2 ? la : lb;
        px(ctx, x, y, 5, 2, look.shirt);
        px(ctx, x + (Math.cos(a) > 0 ? 5 : -2), y, 2, 2, look.skin);
      }
      for (let i = 0; i < 3; i++) {
        const a = this.t * 5 + (i * Math.PI * 2) / 3;
        const x = Math.round(cx + Math.cos(a) * 20);
        const y = Math.round(FIGHT_Y - 12 + Math.sin(a) * 4);
        px(ctx, x, y - 1, 1, 3, '#ffd23f');
        px(ctx, x - 1, y, 3, 1, '#ffd23f');
      }
      if (Math.floor(this.t * 2) % 3 === 0) {
        px(ctx, cx - 10, FIGHT_Y - 26, 21, 9, '#e5433b');
        drawPixelText(ctx, 'POW!', cx - 8, FIGHT_Y - 24, '#ffd23f');
      }
      return;
    }
    // K.O.
    const loserA = f.hp[0] <= f.hp[1];
    const winner = loserA ? lb : la;
    const loser = loserA ? la : lb;
    drawSprite(ctx, fighterSprite(winner, 2), cx + 4, baseY);
    const lying = spriteFromDraw(`ko:${loser.shirt}:${loser.skin}:${loser.hair}`, 26, 10, (k) => {
      k.translate(0, 10);
      k.rotate(-Math.PI / 2);
      k.drawImage(fighterSprite(loser, 0).canvas, 0, 0);
    });
    drawSprite(ctx, lying, cx - 30, FIGHT_Y + 4);
    for (let i = 0; i < 3; i++) {
      const a = this.t * 6 + (i * Math.PI * 2) / 3;
      px(
        ctx,
        Math.round(cx - 28 + Math.cos(a) * 4),
        Math.round(FIGHT_Y + 2 + Math.sin(a) * 2),
        1,
        1,
        '#ffd23f',
      );
    }
  }

  private drawHost(ctx: CanvasRenderingContext2D, game: Game): void {
    const host = [...game.customers.values()].find((c) => c.archetype === 'host');
    if (!host) return;
    const appear =
      host.state === 'leaving'
        ? 1 - clamp(host.stateTime / FLOW.leave, 0, 1)
        : clamp(host.stateTime / 1.2, 0, 1);
    const flickerOff = !this.reducedMotion && noise(Math.floor(this.t * 15), 1, 5) > 0.85;
    if (flickerOff) return;
    const sprite = seatedSprite(host.look, 'normal');
    const x =
      128 - SEATED_W / 2 + (this.reducedMotion ? 0 : Math.round(Math.sin(this.t * 13) * 0.6));
    drawSprite(ctx, sprite, x, STOOL_SPRITE_Y - 3, { alpha: appear });
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.25 * appear;
    const g = ctx.createRadialGradient(128, STOOL_SPRITE_Y + 8, 1, 128, STOOL_SPRITE_Y + 8, 22);
    g.addColorStop(0, '#ffd23f');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(100, STOOL_SPRITE_Y - 16, 56, 50);
    ctx.restore();
  }

  /* ───────────────────────── lighting & the chair ───────────────────────── */

  private drawLighting(ctx: CanvasRenderingContext2D, game: Game): void {
    const hostOn = game.host !== null && game.host.resolved === null;
    if (
      !game.powerOn ||
      (this.powerFlicker > 0 && Math.floor(this.powerFlicker * 20) % 2 === 0 && !this.reducedMotion)
    ) {
      ctx.save();
      ctx.fillStyle = 'rgba(4,6,18,0.82)';
      ctx.fillRect(0, 0, SCENE_W, SCENE_H);
      ctx.globalCompositeOperation = 'lighter';
      const grill = ctx.createLinearGradient(0, SCENE_H, 0, SCENE_H - 40);
      grill.addColorStop(0, 'rgba(255,120,40,0.45)');
      grill.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grill;
      ctx.fillRect(0, SCENE_H - 40, SCENE_W, 40);
      const exit = ctx.createRadialGradient(
        DOOR.x + 10,
        DOOR.y - 2,
        1,
        DOOR.x + 10,
        DOOR.y - 2,
        22,
      );
      exit.addColorStop(0, 'rgba(255,40,40,0.6)');
      exit.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = exit;
      ctx.fillRect(0, 0, 50, 40);
      ctx.restore();
      px(ctx, DOOR.x + 5, DOOR.y - 5, 11, 4, '#ff3b30');
      drawPixelText(ctx, 'EXIT', DOOR.x + 5, DOOR.y - 5, '#ffffff', 0);
      // Eyes in the dark
      for (const c of game.customers.values()) {
        if (c.seatId < 0 || c.seatId >= STOOL_COUNT || !this.seatedVisible(c)) continue;
        const p = seatPosition(c.seatId);
        if (Math.floor(this.t * 1.3 + c.id) % 5 === 0) continue;
        px(ctx, p.x - 3, STOOL_SPRITE_Y + 8, 1, 1, '#ffffff');
        px(ctx, p.x + 2, STOOL_SPRITE_Y + 8, 1, 1, '#ffffff');
      }
      if (this.lightning > 0 && !this.reducedMotion) {
        ctx.globalAlpha = this.lightning * 0.6;
        px(ctx, 0, 0, SCENE_W, SCENE_H, '#dfe8ff');
        ctx.globalAlpha = 1;
      }
    }
    if (hostOn) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = 'rgba(200,40,40,0.9)';
      ctx.fillRect(0, 0, SCENE_W, SCENE_H);
      ctx.restore();
      const v = ctx.createRadialGradient(128, 70, 30, 128, 70, 150);
      v.addColorStop(0, 'rgba(0,0,0,0)');
      v.addColorStop(1, 'rgba(0,0,0,0.75)');
      ctx.fillStyle = v;
      ctx.fillRect(0, 0, SCENE_W, SCENE_H);
      if (!this.reducedMotion && noise(Math.floor(this.t * 9), 2, 8) > 0.6) {
        const y = Math.floor(noise(Math.floor(this.t * 9), 3, 8) * SCENE_H);
        const h = 2 + Math.floor(noise(Math.floor(this.t * 9), 4, 8) * 8);
        const shift = Math.round((noise(Math.floor(this.t * 9), 5, 8) - 0.5) * 12);
        ctx.drawImage(this.canvas, 0, y, SCENE_W, h, shift, y, SCENE_W, h);
      }
    }
    // Dawn warmth
    const dawn = clamp((game.minutes - 420) / 60, 0, 1);
    if (dawn > 0) {
      ctx.fillStyle = `rgba(255,170,90,${0.12 * dawn})`;
      ctx.fillRect(0, 0, SCENE_W, SCENE_H);
    }
  }

  private drawChair(ctx: CanvasRenderingContext2D, game: Game): void {
    const ch = game.chair;
    if (!ch) return;
    const caught = ch.outcome === 'caught' || ch.outcome === 'perfect';
    const tFlight = ch.outcome
      ? caught
        ? ch.duration - (ch.duration - Math.min(ch.t, ch.duration))
        : ch.duration + ch.resolvedT * 0.8
      : ch.t;
    const p = clamp(tFlight / ch.duration, 0, 1.4);
    const sx = FIGHT_X;
    const sy = FIGHT_Y - 8;
    const ex = ch.targetX;
    const ey = 104;
    const k = Math.min(p, 1);
    const x = (1 - k) * (1 - k) * sx + 2 * (1 - k) * k * ((sx + ex) / 2) + k * k * ex;
    const y = (1 - k) * (1 - k) * sy + 2 * (1 - k) * k * 8 + k * k * ey;
    const scale = 1 + 3.2 * easeInQuad(Math.min(p, 1.2));
    const rot = caught ? ch.spin * 0.15 : ch.spin * p * 6;
    const alpha = ch.outcome && !caught ? clamp(1 - ch.resolvedT * 2, 0, 1) : 1;

    // Motion trail
    if (!ch.outcome && !this.reducedMotion) {
      for (let i = 1; i <= 2; i++) {
        const kk = Math.max(0, k - i * 0.05);
        const tx = (1 - kk) * (1 - kk) * sx + 2 * (1 - kk) * kk * ((sx + ex) / 2) + kk * kk * ex;
        const ty = (1 - kk) * (1 - kk) * sy + 2 * (1 - kk) * kk * 8 + kk * kk * ey;
        this.drawRotated(ctx, this.chair, tx, ty, scale * (1 - i * 0.08), ch.spin * kk * 6, 0.18);
      }
    }
    this.drawRotated(ctx, this.chair, x, y, scale, rot, alpha);

    if (!ch.outcome) {
      // Timing ring: meets the chair's outline exactly at the perfect moment.
      const chairR = (this.chair.w * scale) / 2 + 2;
      let r: number;
      if (ch.t <= ch.perfectAt) r = chairR + (1 - ch.t / ch.perfectAt) * 46;
      else r = chairR * (1 - ((ch.t - ch.perfectAt) / (ch.duration - ch.perfectAt)) * 0.6);
      const inWindow = ch.t >= ch.windowStart;
      const nearPerfect = Math.abs(ch.t - ch.perfectAt) <= ch.perfectTolerance;
      ctx.strokeStyle = nearPerfect ? '#51cf66' : inWindow ? '#ffd23f' : 'rgba(255,255,255,0.8)';
      ctx.lineWidth = nearPerfect ? 2 : 1;
      ctx.beginPath();
      ctx.arc(Math.round(x), Math.round(y), Math.max(2, r), 0, Math.PI * 2);
      ctx.stroke();
    } else if (caught) {
      // Hands!
      const half = (this.chair.w * scale) / 2;
      px(ctx, x - half - 4, y - 2, 6, 5, '#e0a47a');
      px(ctx, x + half - 2, y - 2, 6, 5, '#e0a47a');
      px(ctx, x - half - 4, y - 2, 6, 1, '#f3c49f');
      px(ctx, x + half - 2, y - 2, 6, 1, '#f3c49f');
    }
  }

  private drawRotated(
    ctx: CanvasRenderingContext2D,
    s: Sprite,
    x: number,
    y: number,
    scale: number,
    rot: number,
    alpha: number,
  ): void {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(Math.round(x), Math.round(y));
    ctx.rotate(rot);
    ctx.scale(scale, scale);
    ctx.drawImage(s.canvas, -s.w / 2, -s.h / 2);
    ctx.restore();
  }
}
